import { colors, DocumentTagDTO, randomColor } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../../elements/popover";
import { shared } from "../../styles";

@customElement("ptc-document-tags-input")
export class DocumentTagsInput extends LitElement {
    @property({ attribute: false })
    tags: DocumentTagDTO[] = [];

    @property({ attribute: false })
    availableTags: Readonly<DocumentTagDTO[]> = [];

    @state()
    _showResults: boolean = false;

    @query("input")
    private _input: HTMLInputElement;

    @query(".results")
    private _results?: HTMLDivElement;

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener("focus", () => this._focus());
        this.addEventListener("blur", () => this._blur());
    }

    private _keydown(e: KeyboardEvent) {
        if (e.key === "Enter") {
            const newTagName = this._input.value.trim();
            if (newTagName) {
                void this._addTag(
                    new DocumentTagDTO({
                        name: newTagName,
                        color: randomColor(this.availableTags.map((tag) => tag.color)),
                        hideFromEmployees: false,
                        order: this.availableTags.length,
                    })
                );
            }
        }
    }

    private async _addTag(tag: DocumentTagDTO) {
        if (!tag || this.tags.some((t) => t.name === tag.name)) {
            return;
        }

        if (tag.id == null) {
            // give tag a randomized color on creation
            tag.color = randomColor(this.availableTags.map((t) => t.color));
        }

        this.tags.push(tag);
        this._input.value = "";
        this._showResults = false;
        this.requestUpdate();
        await this.updateComplete;
        this._input.focus();
        this.dispatchEvent(new CustomEvent("change", { detail: this.tags }));
    }

    private _tagClicked(tag: DocumentTagDTO) {
        this.tags = this.tags.filter((t) => t.name !== tag.name);
        this.dispatchEvent(new CustomEvent("change", { detail: this.tags }));
    }

    private _hideResultsTimeout: number;

    private _focus() {
        window.clearTimeout(this._hideResultsTimeout);
        if (this._results) {
            this._results.style.display = "";
        }
        this.classList.add("focused");
    }

    private _blur() {
        this.classList.remove("focused");
        this._hideResultsTimeout = window.setTimeout(() => {
            if (this._results) {
                this._results.style.display = "none";
            }
        }, 100);
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: row;
                gap: 0.3em;
                flex-wrap: wrap;
                border: solid 1px var(--shade-2);
                border-radius: 0.5em;
                position: relative;
                z-index: 1;
                overflow: visible;
                padding: 0.3em;
            }

            input {
                padding: 0.3em;
                border: none;
                background: none;
                width: 100%;
            }

            :host(.focused) {
                border-color: var(--color-primary);
            }

            :host(:not(.focused)) ptc-ui-icon[name="icon.action.add"] {
                opacity: 0.5;
            }

            .results {
                position: absolute;
                background: var(--color-bg);
                border: solid 1px var(--shade-2);
                width: 100%;
                border-radius: 0.5em;
                margin-top: 0.2em;
                box-sizing: border-box;
                transition: pointer-events 0.5s;
                max-width: 15em;
            }

            .add-tag {
                overflow: visible;
                position: relative;
                font-size: 0.9em;
            }

            .pill:not(:hover) > .remove-icon {
                display: none;
            }

            .add-tag,
            .add-tag-input {
                flex-grow: 1;
            }
        `,
    ];

    render() {
        const value = this._input?.value || "";
        const results = this.availableTags.filter(
            (t) =>
                !this.tags.some((t2) => t2.name === t.name) &&
                t.name !== value &&
                t.name.toLowerCase().startsWith(value.toLowerCase())
        );

        //this is used to show a placeholder for new tags
        if (value) {
            results.push(
                new DocumentTagDTO({
                    name: value,
                    color: colors.black,
                    hideFromEmployees: false,
                    order: results.length,
                })
            );
        }

        return html`
            ${this.tags.map(
                (tag) => html`
                    <div
                        class="smaller pill click"
                        @click=${() => this._tagClicked(tag)}
                        style="--color-highlight: ${tag.color}"
                    >
                        <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon>
                        ${tag.name}
                        ${tag.hideFromEmployees
                            ? html` <ptc-ui-icon name="icon.toggle.visibility-hidden" class="subtle"></ptc-ui-icon> `
                            : ""}
                        <ptc-ui-icon name="icon.action.remove" class="smaller remove-icon"></ptc-ui-icon>
                    </div>
                `
            )}

            <div class="smaller add-tag">
                <div class="center-aligning horizontal layout">
                    <ptc-ui-icon name="icon.action.add" @click=${() => this._input.focus()}></ptc-ui-icon>

                    <input
                        class="add-tag-input stretch"
                        placeholder="Hinzufügen..."
                        @keydown=${this._keydown}
                        @input=${() => this.requestUpdate()}
                    />
                </div>

                <ptc-popover alignment="auto" .preferAlignment=${["bottom"]} positioning="fixed" trigger="focus">
                    <div class="half-padded vertical layout">
                        ${results.length
                            ? results.map(
                                  (res) => html`
                                      <button
                                          class="text-left-aligning slim transparent"
                                          @click=${() => this._addTag(res)}
                                          style="--color-highlight: ${res.color}"
                                      >
                                          <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon>${res.name}
                                          ${res.hideFromEmployees
                                              ? html`
                                                    <ptc-ui-icon
                                                        name="icon.toggle.visibility-hidden"
                                                        class="subtle"
                                                    ></ptc-ui-icon>
                                                `
                                              : ""}
                                      </button>
                                      ${res.hideFromEmployees
                                          ? html`<ptc-popover class="tooltip" trigger="hover" alignment="right">
                                                <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon>
                                                Dokumente mit dieser Kategorie können nicht vom Mitarbeiter eingesehen
                                                werden.
                                            </ptc-popover>`
                                          : ""}
                                  `
                              )
                            : html`
                                  <div class="smaller padded subtle">Tippen zum Filtern / neue Kategorie anlegen</div>
                              `}
                    </div>
                </ptc-popover>
            </div>
        `;
    }
}
